import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, type DataTableColumn } from '@admin/components/DataTable';
import Pagination from '@admin/components/Pagination';
import { listQuestionQuality } from '../services/questions';
import { ApiError } from '@admin/services/api';
import { KNOWN_CATEGORIES } from './categories';
import { questionLanguageVisibility } from './contentLanguages';
import type { QuestionListItem, QuestionsListResponse } from '@games/word-clash/shared/types';

const PAGE_SIZE = 20;

type LanguageFilter = '' | 'en' | 'tr';

/**
 * Question Quality dashboard (B-2.6).
 *
 * Read-only view of `Question.quality` metrics so content reviewers can find
 * the bottom-N questions and fix systematically. Default sort `funScore ASC`
 * — worst-rated surface first. Filters: language (EN / TR / both) and
 * category. Click-through to the existing question editor.
 *
 * Behind `viewer` minimum admin role. The cron job (B-2.5) populates the
 * underlying numbers; the telemetry (B-2.4) feeds the cron.
 */
export default function QuestionQualityPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const language = (searchParams.get('language') ?? '') as LanguageFilter;
  const category = searchParams.get('category') ?? '';
  const pageRaw = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: QuestionsListResponse }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  const reqCounterRef = useRef(0);
  const fetchData = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    setState({ status: 'loading' });
    try {
      const data = await listQuestionQuality({
        page,
        pageSize: PAGE_SIZE,
        language: language || undefined,
        category: category || undefined,
      });
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'ready', data });
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load quality metrics';
      setState({ status: 'error', message });
    }
  }, [page, language, category]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (!value) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete('page'); // reset pagination on filter change
    setSearchParams(next, { replace: true });
  }

  function changePage(p: number) {
    const next = new URLSearchParams(searchParams);
    if (p === 1) next.delete('page');
    else next.set('page', String(p));
    setSearchParams(next, { replace: true });
  }

  const columns: ReadonlyArray<DataTableColumn<QuestionListItem>> = [
    {
      key: 'funScore',
      header: 'Fun Score',
      cellClassName: 'font-semibold',
      render: (q) => (
        <span data-testid={`fun-score-${q.id}`}>
          {q.quality.funScore.toFixed(1)} / 10
        </span>
      ),
    },
    {
      key: 'prompt',
      header: 'Prompt',
      render: (q) => {
        // An en-only / tr-only question is never served in the other
        // language, so showing both prompts here invites the reviewer to
        // judge content that will never reach a player.
        const { showEnglish, showTurkish } = questionLanguageVisibility(
          q.contentFlags
        );
        return (
          <div>
            {showEnglish ? <div className="text-sm">{q.promptEn}</div> : null}
            {showTurkish ? (
              <div className={showEnglish ? 'text-xs text-zinc-500' : 'text-sm'}>
                {/* Keep the placeholder the pre-gating code had: on a tr-only
                    row an empty prompt would otherwise render a blank cell
                    indistinguishable from a rendering bug. */}
                {q.promptTr || <span className="text-zinc-400">—</span>}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'category',
      header: 'Category',
      render: (q) => <span className="text-xs">{q.category}</span>,
    },
    {
      key: 'flags',
      header: 'Flags',
      render: (q) =>
        q.contentFlags.length > 0 ? (
          <span className="text-xs text-amber-600">
            {q.contentFlags.join(', ')}
          </span>
        ) : (
          <span className="text-xs text-zinc-400">—</span>
        ),
    },
    {
      key: 'avgAnswers',
      header: 'Avg answers',
      render: (q) => q.quality.avgAnswersGiven.toFixed(1),
    },
    {
      key: 'skipCount',
      header: 'Skips',
      render: (q) => q.quality.skipCount.toString(),
    },
    {
      key: 'timeoutRate',
      header: 'Timeout %',
      render: (q) => `${(q.quality.timeoutRateWhenAsked * 100).toFixed(0)}%`,
    },
    {
      key: 'winRate',
      header: 'Win %',
      render: (q) => `${(q.quality.winRateWhenAsked * 100).toFixed(0)}%`,
    },
  ];

  const totalPages =
    state.status === 'ready'
      ? Math.max(1, Math.ceil(state.data.total / PAGE_SIZE))
      : 1;

  return (
    <div className="space-y-4" data-testid="question-quality-page">
      <header>
        <h1 className="text-2xl font-semibold">Question Quality</h1>
        <p className="text-sm text-zinc-500">
          Sorted worst → best by funScore. Click a row to edit the question.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm">
          Language:{' '}
          <select
            value={language}
            onChange={(e) => updateParam('language', e.target.value)}
            data-testid="quality-language-filter"
            className="ml-1 rounded border border-zinc-300 px-2 py-1 text-sm"
          >
            <option value="">Both</option>
            <option value="en">English</option>
            <option value="tr">Turkish</option>
          </select>
        </label>
        <label className="text-sm">
          Category:{' '}
          <select
            value={category}
            onChange={(e) => updateParam('category', e.target.value)}
            data-testid="quality-category-filter"
            className="ml-1 rounded border border-zinc-300 px-2 py-1 text-sm"
          >
            <option value="">All</option>
            {KNOWN_CATEGORIES.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {state.status === 'loading' ? (
        <div className="text-sm text-zinc-500" data-testid="quality-loading">
          Loading...
        </div>
      ) : state.status === 'error' ? (
        <div className="text-sm text-red-600" data-testid="quality-error">
          {state.message}
        </div>
      ) : (
        <>
          <div className="text-xs text-zinc-500">
            {state.data.total.toLocaleString()} questions match · page {page}/
            {totalPages}
          </div>
          <DataTable
            columns={columns}
            rows={state.data.data}
            rowKey={(q) => q.id}
            onRowClick={(q) => navigate(`/questions/${q.id}`)}
            empty={<span>No matching questions.</span>}
            testId="quality-table"
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={changePage}
          />
        </>
      )}
    </div>
  );
}
